// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## uPrefs.js
//
// Sugar for loading & interacting with preferences & data that are stored
// by individual interactives.
//
// Data is defined as records of completed exercises, with percents & dates.
//   It is in the format:
//    id : the exercise id
//    average : the average percent for all performances
//    attempts : number of attempts of that question
//    lastAttemptedAt: timestamp
/* questionHistory = [{
 *    id : the exercise id
 *    average : the average percent for all performances
 *    attempts : number of attempts of that question
 *    lastAttemptedAt: date
 *    history: [
 *      { percent, date, key:value, key:value, etc... }
 *    ]
 * }]
 */
//
// Preferences is defined as user-specific options that should persist from
// between loadings -- such as settings for a practice drill, etc...
//
// GCR TODO: Global preferences (which exist on the user object) can also
// be loaded and set. These might include solfege system, language, etc...
import type {
  UPrefsUpdateRequest,
  QuestionHistoryEntryRequest,
} from 'shared/definitions/uprefs-definitions';
import config from '../config';
import UApi from './uApi';
import utils from '../utils';

export interface QuestionObject {
  id: string | null;
}

interface QuestionHistory {
  /* the exercise id */
  id: string;
  /* the average for all performances of this exercise */
  average: number;
  /* number of attempts */
  attempts: number;
  /* date of last attempt */
  lastAttemptedAt: string;
  history: QuestionHistoryEntryRequest[];
}

interface Prefs {
  [key: string]: any;
}

interface UPrefData {
  questionHistory: QuestionHistory[];
  prefs: Prefs;
}

const reject = () => Promise.reject('Unable to initialize uPref.');

export default class UPrefs {
  uApi: UApi;

  appName: string;

  protected _uPref: UPrefData;

  prefs: Prefs;

  questionHistory: QuestionHistory[];

  // Public constructor
  constructor(appName?: string, success?: Function, failure?: Function) {
    const baseUrl = `${config.apiUrl}/api`;
    this.uApi = new UApi(`${baseUrl}/uprefs`);
    if (typeof appName !== 'undefined') {
      this.setAppName(appName, success, failure);
    }
  }

  // Selects & loads (or creates) the preferences & data for the
  // specified appName:
  setAppName(appName: string, success?: Function, failure?: Function): Promise<any> {
    this.appName = appName;
    // If a new success or failure function is defined,
    // implement it. If not, keep an old one, or create
    // an empty one.
    return this.uApi
      .get('', { appName })
      .then(this._update.bind(this))
      .then((res) => {
        if (success) success(res);
        return res;
      })
      .catch((err) => {
        if (failure) failure(err);
        return Promise.reject(err);
      });
  }

  setPreferences(prefsObj: Prefs) {
    if (typeof this._uPref === 'undefined') return reject();

    if (!utils.isObject(prefsObj)) {
      return Promise.reject('prefsObj must be an object.');
    }
    return this.uApi
      .put('', {
        appName: this.appName,
        prefs: prefsObj,
      })
      .then(this._update.bind(this));
  }

  // Adds a property to uPrefs.prefs model for the current appName.
  setPreference(key: string, value: any) {
    if (typeof this._uPref === 'undefined') return reject();

    const prefs: Prefs = {};
    prefs[key] = value;

    return this.uApi
      .put('', {
        appName: this.appName,
        prefs,
      })
      .then(this._update.bind(this));
  }

  // Gets a property from the uPrefs.prefs model for the current appName.
  getPreference(key: string) {
    if (typeof this._uPref === 'undefined') return reject();
    return this._uPref.prefs[key];
  }

  /**
   * Tests whether a date key was within n hours ago from now.
   *
   * @param {string} key
   * @param numberOfHours
   * @param {number} numberOfMinutes
   * @returns {boolean}
   */
  withinHours(key: string, numberOfHours: number): boolean {
    return this.withinMinutes(key, 60 * numberOfHours);
  }

  /**
   * Tests whether a date key was within n minutes ago from now.
   *
   * @param {string} key
   * @param {number} numberOfMinutes
   * @returns {boolean}
   */
  withinMinutes(key: string, numberOfMinutes: number): boolean {
    const value = this.getPreference(key);
    if (!value) return false;
    const date = new Date(value);
    const now = new Date();
    return now.valueOf() - date.valueOf() < numberOfMinutes * 60 * 1000;
  }

  /* Returns total question history, or history for a particular
   * question ID.
   * @param questionId
   *
   * questionHistory = [{
   *    id : the exercise id
   *    average : the average percent for all performances
   *    attempts : number of attempts of that question
   *    lastAttemptedAt: date
   *    history: [
   *      { percent, date, key:value, key:value, etc... }
   *    ]
   * }]
   */
  getQuestionHistory(): QuestionHistory[];
  getQuestionHistory(questionId: string): QuestionHistory | undefined;
  getQuestionHistory(questionId?) {
    // If no questionId, return all questions:
    if (typeof questionId === 'undefined') {
      return this._uPref.questionHistory || [];
    }
    // Otherwise, return just that question, or false if
    // never performed:
    return this._uPref.questionHistory.find((perf) => perf.id === questionId);
  }

  protected _update(_uPref) {
    this._uPref = _uPref;
    this.prefs = _uPref.prefs;
    this.questionHistory = _uPref.questionHistory;
    return _uPref;
  }

  // ##############################################################
  //
  // Performance Data Routines:
  //
  // For saving performances, and filtering potential performances
  // against a list of those already completed.
  //
  // ##############################################################

  /* saveQuestion(id, percent, infoObj)
   * saveQuestion { id, percent, key:valueForInfoObj, etc... }
   *
   * Usage:
   *   saveQuestion("23", 79, {tempo: 120});
   *     or
   *   saveQuestion({
   *      id: "23",
   *      percent: 79,
   *      tempo: 120 // any additional key:values desired
   *   });
   *
   * The data itself is saved in this format:
   *   questionHistory = [{
   *      id : the exercise id
   *      average : the average percent for all performances
   *      attempts : number of attempts of that question
   *      lastAttemptedAt: date
   *      history: [
   *        { percent, date, key:value, key:value }
   *      ]
   *   }]
   */

  saveQuestion(questionData: {
    id: string;
    percent: number;
    otherData?: { [key: string]: any };
  }): Promise<any>;

  saveQuestion(id: string, percent: number, otherData?: { [key: string]: any }): Promise<any>;
  saveQuestion(idOrObject, percent?: number, infoObj: { [key: string]: any } = {}): Promise<any> {
    if (typeof this._uPref === 'undefined') return reject();
    // Coerce arguments into { id, percent, [?infoObj] }
    let id: string = null;
    if (arguments.length === 1 && utils.isObject(idOrObject)) {
      infoObj = idOrObject;
      id = infoObj.id;
    } else {
      id = idOrObject;
    }

    // Be sure all we need has been provided:
    if (typeof id === 'undefined' || typeof percent === 'undefined') {
      return Promise.reject('An id & percent must be provided to save a score to the database.');
    }

    const updateRequest: UPrefsUpdateRequest = {
      appName: this.appName,
      questionHistory: {
        id,
        percent,
        ...infoObj,
      },
    };

    return this.uApi.put('', updateRequest).then(this._update.bind(this));
  }

  /*
   * questionHistory : {id, percent, key:values}
   * prefs : {}
   *
   */
  saveQuestionAndPrefs(
    questionHistory: QuestionHistoryEntryRequest | QuestionHistoryEntryRequest[],
    prefs: Prefs
  ) {
    return this.uApi
      .put('', {
        appName: this.appName,
        questionHistory,
        prefs,
      })
      .then(this._update.bind(this));
  }

  /**
   * Filters a list of potential questions, removing those that fail the
   * filterFunction from the list.
   *
   * @param questions potential questions to ask
   * @param filter function to filter them
   *
   * If a filterFunction is not specified, any questions already completed
   * are removed from the list.
   *
   *    filterFunction(dataArray, singleQuestion) {
   *      if(questions.id in dataArray) return false;
   *    }
   */
  filter(
    questions: QuestionObject[],
    filter?: (questionHistory: QuestionHistory[], potentialQuestion: QuestionObject) => boolean
  ): QuestionObject[] {
    const data = this.questionHistory;

    if (data.length === 0) return questions;

    // Default filter rule:
    const defaultFilter = (dataArray, question) =>
      !dataArray.some((datum) => datum.id === question.id);

    const filteredQuestions = questions.filter((question) =>
      (filter || defaultFilter)(data, question)
    );

    return filteredQuestions;
  }

  getQuestions(
    questionData: QuestionObject[],
    maxQuestions: number,
    mandatoryFilter?: (
      questionHistory: QuestionHistory[],
      potentialQuestion: QuestionObject
    ) => boolean
  ): QuestionObject[] {
    let questions = this.filter(questionData);
    questions = this.filter(questions, mandatoryFilter);
    if (questions.length < maxQuestions) {
      let recycledQuestions = this.filter(questionData, mandatoryFilter);
      if (recycledQuestions.length === 0) return questions; // there are no
      // matching questions.
      const data = this.questionHistory;
      recycledQuestions = recycledQuestions.sort((a, b) => {
        const aData = data[utils.findIndex(data, (o) => o.id === a.id)];
        const bData = data[utils.findIndex(data, (o) => o.id === b.id)];
        if (typeof aData === 'undefined' && typeof bData === 'undefined') {
          return 0;
        }
        if (typeof aData === 'undefined') return -1;
        if (typeof bData === 'undefined') return 1;
        if (aData.attempts < bData.attempts) return -1;
        if (aData.attempts > bData.attempts) return 1;
        if (aData.average < bData.average) return -1;
        if (aData.average > bData.average) return 1;
        if (aData.lastAttemptedAt < bData.lastAttemptedAt) return -1;
        if (aData.lastAttemptedAt > bData.lastAttemptedAt) return 1;
        return 0;
      });

      // Knock out any that are already in the question list, and add 'em on:
      questions.concat(
        recycledQuestions.filter((q) => !utils.findIndex(questions, (qo) => qo.id === q.id))
      );

      // If the first question of recycledQuestions = the last questions of
      // questions, swap the places:
      if (
        recycledQuestions[0].id === questions[questions.length - 1].id &&
        recycledQuestions.length >= 2
      ) {
        const a = recycledQuestions[1];
        recycledQuestions[1] = recycledQuestions[0];
        recycledQuestions[0] = a;
      }

      // If it's still not long enough, keep repeating the whole shebang:
      while (questions.length < maxQuestions) {
        questions = questions.concat(recycledQuestions);
      }
    }
    return questions;
  }

  // Checks the server for any changes to the uPrefs object, loads them,
  // and updates local variables.
  update() {
    if (typeof this._uPref === 'undefined') return reject();
    return this.uApi.get('', { appName: this.appName }).then(this._update.bind(this));
  }
}
